import { Badge, Box, ChakraAsyncSelect, Flex, HStack, Spinner, Text } from '@elkaso-app/web-design';
import { useSearchMasterProductFamiliesApi } from 'apis/master-product-families';
import { useGetMasterProductsApi } from 'apis/master-products';
import { PriceImportRow } from 'apis/price-imports';
import { useGetAttributeDictionaryApi } from 'apis/product-attributes';
import { SimilarVariantCards } from 'pages/inquiry-processor/components/quote-matcher/similar-variant-cards';
import { useState } from 'react';

export interface RematchPanelProps {
  row: PriceImportRow;
  onPickMaster: (masterProductId: number) => void;
}

/**
 * Pick the right product for a row the matcher could not resolve.
 *
 * Three affordances, in the order product-matcher.tsx ranks them: the
 * candidates the backend already found, then a search across the whole
 * catalogue, and only then the proposal the row is carrying. Whatever the
 * operator picks becomes a PATCH of masterProductId — the backend re-derives
 * the price rung against THAT product's ladder, so nobody has to think about
 * rungs to correct a match.
 *
 * Rendered as an expanding row rather than a modal: the surrounding rows are
 * the context for deciding, and a modal would hide them.
 */
export const RematchPanel = ({ row, onPickMaster }: RematchPanelProps) => {
  // Seeded from the row's best candidate, so the common case is one click.
  const [familyId, setFamilyId] = useState<number | null>(row.familyId ?? row.candidates?.[0]?.familyId ?? null);
  const [familyLabel, setFamilyLabel] = useState<string | null>(row.candidates?.[0]?.universalName ?? null);

  const { loadOptions } = useSearchMasterProductFamiliesApi();
  const variantsQuery = useGetMasterProductsApi(familyId ?? undefined, { enabled: !!familyId });
  const { data: dictionaryData } = useGetAttributeDictionaryApi();

  const variants = variantsQuery.data?.data ?? [];
  const candidates = row.candidates ?? [];

  /** The row's own attributes, so the closest variants sort to the top. */
  const ask = {
    brand: row.canonical.brand ?? null,
    origin: row.canonical.origin ?? null,
    state: row.canonical.state ?? null,
  };

  return (
    <Box bg='gray.50' borderTop='1px solid' borderColor='gray.200' p='14px'>
      <Text fontSize='xs' color='gray.500' mb='10px'>
        Supplier wrote: <b>{row.canonical.source_name ?? '—'}</b>
        {row.canonical.brand ? ` · ${row.canonical.brand}` : ''}
        {row.canonical.origin ? ` · ${row.canonical.origin}` : ''}
        {row.canonical.state ? ` · ${row.canonical.state}` : ''}
      </Text>

      {candidates.length > 0 && (
        <Box mb='12px'>
          <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb='6px'>
            Closest families
          </Text>
          <HStack spacing='6px' wrap='wrap'>
            {candidates.map((candidate) => {
              const fits = candidate.variants.filter((variant) => !variant.gatedOut).length;
              const isActive = familyId === candidate.familyId;

              return (
                <Box
                  key={candidate.familyId}
                  as='button'
                  type='button'
                  onClick={() => {
                    setFamilyId(candidate.familyId);
                    setFamilyLabel(candidate.universalName);
                  }}
                  px='10px'
                  py='5px'
                  borderRadius='8px'
                  border='1px solid'
                  borderColor={isActive ? 'red.400' : 'gray.200'}
                  bg={isActive ? 'red.50' : 'white'}
                  textAlign='left'>
                  <Text fontSize='sm' fontWeight={isActive ? 'semibold' : 'medium'}>
                    {candidate.universalName}
                  </Text>
                  <Text fontSize='10.5px' color='gray.500'>
                    {Math.round(candidate.score)}% name match ·{' '}
                    {fits > 0 ? `${fits} variant${fits === 1 ? '' : 's'} fit` : 'none fit'}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        </Box>
      )}

      <Box mb='12px' maxW='420px'>
        <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb='6px'>
          Or search the whole catalogue
        </Text>
        <ChakraAsyncSelect
          size='sm'
          placeholder='Search families…'
          loadOptions={loadOptions}
          menuPortalTarget={document.body}
          menuPosition='fixed'
          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
          onChange={(option: any) => {
            setFamilyId(option?.value ?? null);
            setFamilyLabel(option?.label ?? null);
          }}
        />
      </Box>

      {familyId && (
        <Box>
          <Flex align='center' gap='8px' mb='6px'>
            <Text fontSize='xs' fontWeight='semibold' color='gray.600'>
              Variants of {familyLabel ?? `family #${familyId}`}
            </Text>
            {variantsQuery.isLoading && <Spinner size='xs' />}
          </Flex>

          {!variantsQuery.isLoading && variants.length === 0 && (
            <Text fontSize='sm' color='gray.500'>
              This family has no variants yet.
            </Text>
          )}

          {variants.length > 0 && (
            <>
              <SimilarVariantCards
                variants={variants}
                dictionary={dictionaryData?.data}
                ask={ask}
                onPick={(variant) => onPickMaster(variant.id)}
              />
              {/* useGetMasterProductsApi caps at 100 — the same limitation
                  product-matcher.tsx carries. Say so rather than silently
                  truncating the list. */}
              {variants.length >= 100 && (
                <Text fontSize='11px' color='orange.700' mt='6px'>
                  Showing the first 100 variants. Narrow the family if the one you need is missing.
                </Text>
              )}
            </>
          )}
        </Box>
      )}

      {row.proposal?.master && (
        <Box mt='12px' pt='10px' borderTop='1px dashed' borderColor='gray.300'>
          <Text fontSize='xs' color='gray.600'>
            Nothing fits? Approving this row creates{' '}
            <Badge colorScheme='purple' variant='subtle'>
              {row.proposal.family ? `a new family “${row.proposal.family.universalName}”` : 'a new variant'}
            </Badge>{' '}
            when the import is committed.
          </Text>
        </Box>
      )}
    </Box>
  );
};
